import React from 'react';
import {
    Grid,
    Paper,
    Typography,
    Box,
    LinearProgress,
    Card,
    CardContent,
    Divider
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

// ========== استایل‌ها ==========
const useStyles = makeStyles((theme) => ({
    root: {
        padding: 20,
        backgroundColor: '#f9f9f9',
        borderRadius: 16,
        marginTop: 16,
        marginBottom: 16,
        direction: 'rtl',
    },
    card: {
        height: '100%',
        transition: 'all 0.3s ease',
        borderRadius: 12,
        '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        },
    },
    cardContent: {
        padding: '16px !important',
    },
    label: {
        fontSize: '0.75rem',
        fontWeight: 600,
        color: '#888',
        letterSpacing: '0.5px',
        marginBottom: 6,
        textTransform: 'uppercase',
    },
    value: {
        fontSize: '1rem',
        fontWeight: 500,
        color: '#2c3e50',
        wordBreak: 'break-word',
    },
    discountCard: {
        backgroundColor: '#e3f2fd',
        '&:hover': {
            backgroundColor: '#bbdefb',
        },
    },
    sectionTitle: {
        backgroundColor: '#1976d2',
        color: 'white',
        padding: '8px 16px',
        borderRadius: 8,
        fontSize: '1rem',
        fontWeight: 600,
        width: 'fit-content',
    },
    sectionWrapper: {
        marginTop: 20,
        marginBottom: 10,
    },
    divider: {
        margin: '16px 0',
    },
}));

// ========== تابع کمکی برای استخراج درصد ==========
const getDiscountPercent = (discountString) => {
    if (!discountString) return 0;
    const match = discountString ? discountString.match(/\((-?\d+)%\)/) : null;
    return match ? Math.max(0, parseFloat(match[1])) : 0;
};

// ========== تابع کمکی برای بررسی وجود مقدار ==========
const hasValue = (value) => {
    return value !== null && value !== undefined && value !== '';
};

// ========== کامپوننت InfoCard ==========
const InfoCard = ({ label, value, className = '' }) => {
    const classes = useStyles();
    if (!hasValue(value)) return null;

    return (
        <Card className={`${classes.card} ${className}`} variant="outlined">
            <CardContent className={classes.cardContent}>
                <Typography className={classes.label}>{label}</Typography>
                <Typography className={classes.value}>
                    {value}
                </Typography>
            </CardContent>
        </Card>
    );
};

// ========== کامپوننت DiscountCard ==========
const DiscountCard = ({ label, value, progressValue }) => {
    const classes = useStyles();
    if (!hasValue(value) && progressValue === 0) return null;

    return (
        <Card className={`${classes.card} ${classes.discountCard}`} variant="outlined">
            <CardContent className={classes.cardContent}>
                <Typography className={classes.label}>{label}</Typography>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box style={{ flexGrow: 1 }}>
                        <LinearProgress
                            variant="determinate"
                            value={Math.max(0, Math.min(100, progressValue))}
                            style={{ height: 8, borderRadius: 4 }}
                        />
                    </Box>
                    <Typography variant="body2" style={{ fontWeight: 600, color: '#1976d2' }}>
                        {progressValue}%
                    </Typography>
                </Box>
                <Typography className={classes.value} style={{ marginTop: 8 }}>
                    {value || '—'}
                </Typography>
            </CardContent>
        </Card>
    );
};

// ========== کامپوننت اصلی ==========
const InsuranceDetailsCard = ({ data }) => {
    const classes = useStyles();

    if (!data) return null;

    // تعریف بخش‌ها با فیلدهای مربوطه
    const sections = [
        {
            title: 'اطلاعات اکسل اولیه',
            icon: '📋',
            fields: [
                { label: 'تاریخ شروع همکاری', value: data.cooperation_start_date },
                { label: 'تاریخ صدور', value: data.issue_date },
                { label: 'نوع بیمه‌نامه (متن)', value: data.string },
                { label: 'شماره بیمه نامه', value: data.policy_number },
            ]
        },
        {
            title: 'اطلاعات خودرو',
            icon: '🚗',
            fields: [
                { label: 'نام خودرو', value: data.vehicalName },
                { label: 'شماره پلاک', value: data.plateNumber },
                { label: 'سال تولید', value: data.productionDate },
                { label: 'شماره موتور', value: data.engineNumber },
                { label: 'شماره شاسی', value: data.chassisNo },
                { label: 'کد VIN', value: data.vin },
                { label: 'رکورد دارای ثالث و بدنه میباشد؟', value: data.has_body_and_third },
            ]
        },
        {
            title: 'اطلاعات بیمه‌گذار',
            icon: '👤',
            fields: [
                { label: 'نام بیمه‌گذار', value: data.policyholder },
                { label: 'کد ملی', value: data.nationalCode },
                { label: 'شماره موبایل', value: data.phone_number },
                { label: 'شماره پرسنلی', value: data.personnel_no },
                { label: 'نام مدیریت', value: data.management_name },
                { label: 'نام شرکت', value: data.company_name },
            ]
        },
        {
            title: 'اطلاعات سرپرست',
            icon: '👨‍👩‍👧‍👦',
            fields: [
                { label: 'نسبت با سرپرست', value: data.relation },
                { label: 'نام سرپرست', value: data.parent_name },
            ]
        },
        {
            title: 'تخفیف‌ها',
            icon: '💰',
            isDiscount: true,
            fields: [
                { label: 'تخفیف مالی', value: data.discountFinance, progressValue: getDiscountPercent(data.discountFinance) },
                { label: 'تخفیف جانی', value: data.discountLife, progressValue: getDiscountPercent(data.discountLife) },
                { label: 'تخفیف راننده', value: data.discountDriver, progressValue: getDiscountPercent(data.discountDriver) },
            ]
        },
        {
            title: 'اطلاعات بیمه‌نامه‌ها',
            icon: '🔄',
            fields: [
                { label: 'بیمه‌گر', value: data.insurer },
                { label: 'نوع بیمه‌نامه', value: data.typePolicy },
                { label: 'تاریخ شروع', value: data.beginDate },
                { label: 'شماره بیمه‌نامه قبلی', value: data.lastPolicyNumber },
                { label: 'شماره بیمه‌نامه فعلی', value: data.policyNumber },
                { label: 'کد یکتای بیمه‌نامه', value: data.policyUniqueCode },
            ]
        },
        {
            title: 'اطلاعات تکمیلی',
            icon: '📌',
            fields: [
                { label: 'معرف', value: data.representation },
                { label: 'عدم خسارت اصلی', value: data.no_major_damage },
                { label: 'عدم خسارت فرعی', value: data.no_additional_damage },
                { label: 'تاریخ پایان شلر', value: data.sheller_end_date },
            ]
        },
        {
            title: 'توضیحات تماس بعدی',
            icon: '📝',
            fields: [
                { label: 'توضیح تماس بعدی', value: data.call_back_desc },
            ]
        }
    ];

    // رندر کردن یک بخش
    const renderSection = (section) => {
        // بررسی وجود حداقل یک مقدار در بخش
        let hasAnyValue = false;
        for (const field of section.fields) {
            if (section.isDiscount) {
                if (hasValue(field.value) && field.progressValue > 0) {
                    hasAnyValue = true;
                    break;
                }
            } else {
                if (hasValue(field.value)) {
                    hasAnyValue = true;
                    break;
                }
            }
        }

        if (!hasAnyValue) return null;

        return (
            <React.Fragment key={section.title}>
                <div className={classes.sectionWrapper}>
                    <Typography className={classes.sectionTitle}>
                        {section.icon} {section.title}
                    </Typography>
                </div>
                <Grid container spacing={2}>
                    {section.fields.map((field, idx) => {
                        if (section.isDiscount) {
                            if (!hasValue(field.value) && field.progressValue === 0) return null;
                            return (
                                <Grid item xs={12} sm={6} md={4} key={idx}>
                                    <DiscountCard
                                        label={field.label}
                                        value={field.value}
                                        progressValue={field.progressValue}
                                    />
                                </Grid>
                            );
                        } else {
                            if (!hasValue(field.value)) return null;
                            return (
                                <Grid item xs={12} sm={6} md={4} key={idx}>
                                    <InfoCard label={field.label} value={field.value} />
                                </Grid>
                            );
                        }
                    })}
                </Grid>
                <Divider className={classes.divider} />
            </React.Fragment>
        );
    };

    return (
        <Paper className={classes.root} elevation={0}>
            {sections.map((section) => renderSection(section))}
        </Paper>
    );
};

export default InsuranceDetailsCard;